// @ts-nocheck
/**
 * ツインキーシステム - メインハブ画面
 * @module twinKey/ui/screens/TwinKeyHub
 */

import React, { useEffect } from 'react';
import { useTwinKeyContext, SCREENS } from '../../context/TwinKeyContext';
import { useTwinKey } from '../../hooks';
import { hasCard } from '../../../collection/systems/collectionManager';
import styles from '../styles/twinKeyStyles';

/**
 * メインハブ画面
 * 鍵選択・パーティ編成・探索開始の入口
 */
export function TwinKeyHub() {
  const { state, actions, playerData, onBack, allCards } = useTwinKeyContext();
  const { goToKeySelection, goToParty, canStartExpedition, startExpedition, clearKey } = useTwinKey();

  // 保存されたパーティを読み込む
  useEffect(() => {
    if (!state.activeParty && playerData?.twinKey) {
      const { parties, activePartyId } = playerData.twinKey;
      if (parties && parties.length > 0) {
        // activePartyIdで指定されたパーティ、なければ最初のパーティを読み込む
        const partyToLoad = activePartyId
          ? parties.find(p => p.id === activePartyId) || parties[0]
          : parties[0];
        if (partyToLoad) {
          console.log('[TwinKeyHub] 保存されたパーティを読み込み:', partyToLoad);
          actions.setActiveParty(partyToLoad);
        }
      }
    }
  }, [state.activeParty, playerData, actions]);

  // 保存された鍵を復元（リロード対応）
  useEffect(() => {
    if (!playerData?.twinKey || !allCards || allCards.length === 0) return;
    if (!playerData.collection) return;

    const { lastKey1, lastKey2 } = playerData.twinKey;
    let restoredKey1 = null;
    let restoredKey2 = null;

    // key1の復元（state.key1がnullで、lastKey1がある場合）
    if (!state.key1 && lastKey1 && lastKey1.cardId) {
      // 所持確認
      if (hasCard(playerData, lastKey1.cardId, lastKey1.rarity)) {
        const baseCard = allCards.find(c => c.id === lastKey1.cardId);
        if (baseCard) {
          restoredKey1 = {
            ...baseCard,
            rarity: lastKey1.rarity,
            uniqueKey: `${lastKey1.cardId}_${lastKey1.rarity}`,
          };
          console.log('[TwinKeyHub] 鍵1を復元:', restoredKey1.name);
          actions.setKey1(restoredKey1);
        }
      }
    }

    // key2の復元（state.key2がnullで、lastKey2がある場合）
    if (!state.key2 && lastKey2 && lastKey2.cardId) {
      // 所持確認
      if (hasCard(playerData, lastKey2.cardId, lastKey2.rarity)) {
        const baseCard = allCards.find(c => c.id === lastKey2.cardId);
        if (baseCard) {
          restoredKey2 = {
            ...baseCard,
            rarity: lastKey2.rarity,
            uniqueKey: `${lastKey2.cardId}_${lastKey2.rarity}`,
          };
          console.log('[TwinKeyHub] 鍵2を復元:', restoredKey2.name);
          actions.setKey2(restoredKey2);
        }
      }
    }

    // 両方の鍵が揃った場合、ダンジョンプレビューを生成
    const finalKey1 = restoredKey1 || state.key1;
    const finalKey2 = restoredKey2 || state.key2;
    if (finalKey1 && finalKey2 && !state.dungeonPreview) {
      const { generateDungeonPreview } = require('../../core/dungeon');
      const preview = generateDungeonPreview(finalKey1, finalKey2);
      actions.setDungeonPreview(preview);
    }
  }, [playerData, allCards, state.key1, state.key2, state.dungeonPreview, actions]);

  // 選択中の鍵がコレクションに存在するかチェックし、なければクリア
  useEffect(() => {
    if (!playerData?.collection) return;

    // key1の所持確認
    if (state.key1) {
      const hasKey1 = hasCard(playerData, state.key1.id, state.key1.rarity);
      if (!hasKey1) {
        console.log('[TwinKeyHub] 鍵1が所持されていないためクリア:', state.key1.name);
        clearKey(1);
      }
    }

    // key2の所持確認
    if (state.key2) {
      const hasKey2 = hasCard(playerData, state.key2.id, state.key2.rarity);
      if (!hasKey2) {
        console.log('[TwinKeyHub] 鍵2が所持されていないためクリア:', state.key2.name);
        clearKey(2);
      }
    }
  }, [playerData, state.key1, state.key2, clearKey]);

  // 選択中の鍵情報
  const key1 = state.key1;
  const key2 = state.key2;

  // 探索履歴
  const expeditionHistory = playerData?.twinKey?.expeditionHistory || [];
  const totalExpeditions = expeditionHistory.length;
  const victories = expeditionHistory.filter(e => e.result === 'victory').length;

  return (
    <div style={styles.hubContainer}>
      <p style={{ textAlign: 'center', color: '#ffd700' }}>探索G: {(playerData?.twinKey?.gold || 0).toLocaleString()} G<br />
        <small>探索・育成専用の端末内通貨です。共通Gには交換できません。</small></p>
      {/* ヘッダー（固定） */}
      <div style={styles.hubHeader}>
        <button style={styles.backButton} onClick={onBack}>
          ← 戻る
        </button>
        <div style={styles.hubHeaderCenter}>
          <h1 style={styles.hubTitle}>異界の扉</h1>
          <div style={styles.subtitle}>二枚鍵システム</div>
        </div>
        <div style={styles.hubHeaderSpacer} />
      </div>

      {/* メインコンテンツ（スクロール可能） */}
      <div style={styles.hubScrollContent}>
        <div style={styles.hubScrollContentInner}>
        {/* 鍵選択エリア */}
        <div style={styles.hubSection}>
          <h2 style={styles.sectionTitle}>🔑 鍵の選択</h2>
          <div style={styles.keySlots}>
            <KeySlot
              keyCard={key1}
              slotNumber={1}
              onClick={goToKeySelection}
            />
            <div style={styles.keyPlusIcon}>+</div>
            <KeySlot
              keyCard={key2}
              slotNumber={2}
              onClick={goToKeySelection}
            />
          </div>
          {key1 && key2 && (
            <div style={styles.dungeonPreview}>
              <DungeonPreview key1={key1} key2={key2} />
            </div>
          )}
          {/* 鍵クリアボタン */}
          {(key1 || key2) && (
            <button
              style={{
                ...styles.secondaryButton,
                marginTop: '12px',
                padding: '8px 16px',
                fontSize: '14px',
              }}
              onClick={() => clearKey()}
            >
              🔄 鍵をクリア
            </button>
          )}
        </div>

        {/* パーティエリア */}
        <div style={styles.hubSection}>
          <h2 style={styles.sectionTitle}>⚔️ パーティ</h2>
          <PartyPreview onEdit={goToParty} />
        </div>

        {/* 統計エリア */}
        <div style={styles.hubSection}>
          <h2 style={styles.sectionTitle}>📊 統計</h2>
          <div style={styles.statsGrid}>
            <StatItem label="総探索回数" value={totalExpeditions} />
            <StatItem label="クリア回数" value={victories} />
            <StatItem
              label="クリア率"
              value={totalExpeditions > 0
                ? `${Math.floor(victories / totalExpeditions * 100)}%`
                : '-'
              }
            />
          </div>
        </div>

        {/* 施設エリア */}
        <div style={styles.hubSection}>
          <h2 style={styles.sectionTitle}>🏛️ 施設</h2>
          <div style={styles.facilityButtons}>
            <button
              style={styles.facilityButton}
              onClick={() => actions.setScreen(SCREENS.ALCHEMY)}
            >
              <span style={styles.facilityIcon}>⚗️</span>
              <span style={styles.facilityLabel}>魔導錬金所</span>
            </button>
          </div>
        </div>

        {/* 属性相性エリア */}
        <div style={styles.hubSection}>
          <h2 style={styles.sectionTitle}>⚡ 属性相性</h2>
          <AttributeAffinityChart />
        </div>
        </div>
      </div>

      {/* フッター（固定） - 探索開始ボタン */}
      <div style={styles.hubFooter}>
        <button
          style={{
            ...styles.primaryButton,
            ...(canStartExpedition ? {} : styles.disabledButton),
            width: '100%',
            maxWidth: '300px',
          }}
          onClick={startExpedition}
          disabled={!canStartExpedition}
        >
          🚀 探索開始
        </button>
        {!canStartExpedition && (
          <div style={styles.warningText}>
            {!key1 || !key2
              ? '2枚の鍵を選択してください'
              : 'パーティを編成してください'
            }
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * 鍵スロットコンポーネント
 */
function KeySlot({ keyCard, slotNumber, onClick }) {
  const isEmpty = !keyCard;

  return (
    <div
      style={{
        ...styles.keySlot,
        ...(isEmpty ? styles.emptyKeySlot : {}),
      }}
      onClick={onClick}
    >
      {isEmpty ? (
        <div style={styles.emptySlotContent}>
          <div style={styles.emptySlotIcon}>🔑</div>
          <div style={styles.emptySlotText}>鍵{slotNumber}を選択</div>
        </div>
      ) : (
        <KeyCardPreview card={keyCard} />
      )}
    </div>
  );
}

/**
 * 鍵カードプレビュー
 */
function KeyCardPreview({ card }) {
  const attrColor = styles.getAttributeColor(card.attribute);
  const rarityColor = styles.getRarityColor(card.rarity);

  return (
    <div style={styles.keyCardPreview}>
      <div
        style={{
          ...styles.keyCardAttribute,
          background: attrColor,
        }}
      >
        {styles.getAttributeIcon(card.attribute)}
      </div>
      <div style={styles.keyCardInfo}>
        <div
          style={{
            ...styles.keyCardName,
            color: rarityColor,
          }}
        >
          {card.name}
        </div>
        <div style={styles.keyCardRarity}>{card.rarity}</div>
      </div>
    </div>
  );
}

/**
 * ダンジョンプレビュー
 */
function DungeonPreview({ key1, key2 }) {
  // ダンジョン情報を計算
  const { generateDungeon } = require('../../core/dungeon');
  const dungeon = generateDungeon(key1, key2);

  // 難易度文字列から星の数を取得
  const difficultyStars = {
    '初級': 1,
    '中級': 2,
    '上級': 3,
    '超級': 5,
  };
  const stars = difficultyStars[dungeon.difficulty] || 1;

  return (
    <div style={styles.dungeonPreviewContent}>
      <div style={styles.dungeonPreviewTitle}>
        {dungeon.name}
      </div>
      <div style={styles.dungeonPreviewStats}>
        <span>深度: {dungeon.depthName}</span>
        <span>階層: {dungeon.maxFloor}F</span>
        <span>難易度: {'★'.repeat(stars)} ({dungeon.difficulty})</span>
      </div>
    </div>
  );
}

/**
 * パーティプレビュー
 */
function PartyPreview({ onEdit }) {
  const { state } = useTwinKeyContext();
  const party = state.activeParty; // activeParty を使用（party ではない）

  if (!party || !party.members || party.members.length === 0) {
    return (
      <div style={styles.emptyParty} onClick={onEdit}>
        <div style={styles.emptyPartyIcon}>👥</div>
        <div style={styles.emptyPartyText}>パーティを編成</div>
      </div>
    );
  }

  return (
    <div style={styles.partyPreview} onClick={onEdit}>
      {party.members.map((member, index) => (
        <div
          key={member.uniqueId || index}
          style={{
            ...styles.partyMemberPreview,
            borderColor: styles.getAttributeColor(member.attribute),
          }}
        >
          <div style={styles.memberIcon}>
            {styles.getAttributeIcon(member.attribute)}
          </div>
          <div style={styles.memberName}>{member.name}</div>
          <div style={styles.memberLevel}>Lv.{member.level}</div>
        </div>
      ))}
      <div style={styles.editPartyHint}>タップして編集</div>
    </div>
  );
}

/**
 * 統計アイテム
 */
function StatItem({ label, value }) {
  return (
    <div style={styles.statItem}>
      <div style={styles.statLabel}>{label}</div>
      <div style={styles.statValue}>{value}</div>
    </div>
  );
}

/**
 * 属性相性チャート
 */
function AttributeAffinityChart() {
  const affinityStyles = {
    container: {
      background: 'rgba(0, 0, 0, 0.3)',
      borderRadius: '8px',
      padding: '16px',
    },
    triangleRow: {
      display: 'flex',
      justifyContent: 'space-around',
      marginBottom: '16px',
      gap: '16px',
      flexWrap: 'wrap',
    },
    triangle: {
      flex: '1 1 140px',
      maxWidth: '180px',
      textAlign: 'center',
    },
    triangleTitle: {
      fontSize: '12px',
      color: '#aaa',
      marginBottom: '8px',
    },
    triangleContent: {
      position: 'relative',
      height: '100px',
    },
    attrNode: {
      position: 'absolute',
      width: '36px',
      height: '36px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '18px',
      fontWeight: 'bold',
      border: '2px solid rgba(255,255,255,0.3)',
    },
    arrow: {
      position: 'absolute',
      color: '#4ade80',
      fontSize: '14px',
    },
    rivalrySection: {
      borderTop: '1px solid rgba(255,255,255,0.1)',
      paddingTop: '12px',
      marginTop: '8px',
    },
    rivalryTitle: {
      fontSize: '12px',
      color: '#aaa',
      marginBottom: '8px',
      textAlign: 'center',
    },
    rivalryContent: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '12px',
    },
    rivalryAttr: {
      width: '40px',
      height: '40px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '20px',
      fontWeight: 'bold',
    },
    rivalryArrow: {
      color: '#fbbf24',
      fontSize: '16px',
    },
    legend: {
      marginTop: '12px',
      padding: '8px',
      background: 'rgba(0,0,0,0.2)',
      borderRadius: '4px',
      fontSize: '11px',
      color: '#888',
    },
    legendRow: {
      display: 'flex',
      justifyContent: 'center',
      gap: '16px',
      flexWrap: 'wrap',
    },
    legendItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
    },
  };

  return (
    <div style={affinityStyles.container}>
      {/* 2つの三すくみ */}
      <div style={affinityStyles.triangleRow}>
        {/* 自然系 */}
        <div style={affinityStyles.triangle}>
          <div style={affinityStyles.triangleTitle}>自然系</div>
          <div style={affinityStyles.triangleContent}>
            {/* 炎（上） */}
            <div style={{ ...affinityStyles.attrNode, top: 0, left: '50%', transform: 'translateX(-50%)', background: '#ef4444' }}>
              🔥
            </div>
            {/* 水（左下） */}
            <div style={{ ...affinityStyles.attrNode, bottom: 0, left: '10%', background: '#3b82f6' }}>
              💧
            </div>
            {/* 原始（右下） */}
            <div style={{ ...affinityStyles.attrNode, bottom: 0, right: '10%', background: '#22c55e' }}>
              🌿
            </div>
            {/* 矢印 */}
            <div style={{ ...affinityStyles.arrow, top: '35%', left: '25%', transform: 'rotate(-60deg)' }}>→</div>
            <div style={{ ...affinityStyles.arrow, bottom: '5%', left: '50%', transform: 'translateX(-50%)' }}>←</div>
            <div style={{ ...affinityStyles.arrow, top: '35%', right: '25%', transform: 'rotate(60deg)' }}>→</div>
          </div>
        </div>

        {/* 神秘系 */}
        <div style={affinityStyles.triangle}>
          <div style={affinityStyles.triangleTitle}>神秘系</div>
          <div style={affinityStyles.triangleContent}>
            {/* 光（上） */}
            <div style={{ ...affinityStyles.attrNode, top: 0, left: '50%', transform: 'translateX(-50%)', background: '#eab308' }}>
              ✨
            </div>
            {/* 闇（左下） */}
            <div style={{ ...affinityStyles.attrNode, bottom: 0, left: '10%', background: '#7c3aed' }}>
              🌙
            </div>
            {/* 未来（右下） */}
            <div style={{ ...affinityStyles.attrNode, bottom: 0, right: '10%', background: '#06b6d4' }}>
              ⚡
            </div>
            {/* 矢印 */}
            <div style={{ ...affinityStyles.arrow, top: '35%', left: '20%', transform: 'translateX(50%) rotate(120deg)' }}>→</div>
            <div style={{ ...affinityStyles.arrow, bottom: '5%', left: '50%', transform: 'translateX(-50%) rotate(180deg)' }}>←</div>
            <div style={{ ...affinityStyles.arrow, top: '35%', right: '30%' , transform: 'rotate(-130deg)' }}>→</div>
          </div>
        </div>
      </div>

      {/* 対立軸 */}
      <div style={affinityStyles.rivalrySection}>
        <div style={affinityStyles.rivalryTitle}>⚔️ 時空の対立（×1.25）</div>
        <div style={affinityStyles.rivalryContent}>
          <div style={{ ...affinityStyles.rivalryAttr, background: '#22c55e' }}>🌿</div>
          <div style={affinityStyles.rivalryArrow}>⇄</div>
          <div style={{ ...affinityStyles.rivalryAttr, background: '#06b6d4' }}>⚡</div>
        </div>
      </div>

      {/* 凡例 */}
      <div style={affinityStyles.legend}>
        <div style={affinityStyles.legendRow}>
          <span style={affinityStyles.legendItem}>
            <span style={{ color: '#4ade80' }}>→</span> 有利 ×1.5
          </span>
          <span style={affinityStyles.legendItem}>
            <span style={{ color: '#f87171' }}>←</span> 不利 ×0.67
          </span>
          <span style={affinityStyles.legendItem}>
            <span style={{ color: '#fbbf24' }}>⇄</span> 対立 ×1.25
          </span>
        </div>
      </div>
    </div>
  );
}

export default TwinKeyHub;
